import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Button,
  Empty,
  List,
  Popover,
  Spin,
  Typography,
} from "antd";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { INotification } from "../../models/Notification";

interface NotificationProps {
  notifications: INotification[];
  onNotificationClick?: (notification: INotification) => void;
  hasNextPage?: boolean;
  isFetching?: boolean;
  onLoadMore?: () => void;
}

const timeAgo = (dateLike: Date | string) => {
  const d = new Date(dateLike);
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s trước`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} phút trước`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  return `${days} ngày trước`;
};

const iconForType = (type: INotification["type"]) => {
  switch (type) {
    case "success":
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
    case "warning":
      return <WarningOutlined style={{ color: "#f0aa1fff" }} />;
    case "error":
      return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
    default:
      return <InfoCircleOutlined style={{ color: "#1890ff" }} />;
  }
};

const Notification: FC<NotificationProps> = ({
  notifications,
  onNotificationClick,
  hasNextPage,
  isFetching,
  onLoadMore,
}) => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const notificationList = (
    <div style={{ width: 400, borderRadius: 6, overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <BellOutlined style={{ fontSize: 18 }} />
        <div style={{ flex: 1 }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Thông báo
          </Typography.Title>
        </div>
      </div>

      {notifications?.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Empty description={<span>Không có thông báo</span>} />
        </div>
      ) : (
        <div
          style={{ maxHeight: 360, overflowY: "auto" }}
          onScroll={(e) => {
            const target = e.currentTarget as HTMLDivElement;
            if (
              target.scrollHeight - target.scrollTop - target.clientHeight <
                50 &&
              hasNextPage &&
              !isFetching &&
              onLoadMore
            ) {
              onLoadMore();
            }
          }}
        >
          <List
            dataSource={notifications}
            itemLayout="horizontal"
            renderItem={(item) => (
              <List.Item
                style={{
                  padding: 12,
                  borderBottom: "1px solid #f5f5f5",
                  cursor: "pointer",
                }}
                className={
                  item.isRead
                    ? "dark:bg-[var(--bg-secondary)] bg-[var(--bg-secondary)] "
                    : "dark:bg-[var(--bg-primary)] "
                }
                onClick={() => {
                  onNotificationClick && onNotificationClick(item);
                  navigate(item.link || "/");
                  setVisible(false);
                }}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{ background: "#f5f5f5" }}
                      icon={iconForType(item.type)}
                    />
                  }
                  title={
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span>{item.title}</span>
                    </div>
                  }
                  description={
                    <div style={{ color: "var(--primary-color)" }}>
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 400,
                          fontSize: 14,
                        }}
                      >
                        {item.message}
                      </div>
                      <div
                        style={{ fontSize: 12, color: "#888", marginTop: 6 }}
                      >
                        {timeAgo(item.createdAt)}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
          <div style={{ padding: 8, textAlign: "center" }}>
            {isFetching ? (
              <Spin size="small" />
            ) : hasNextPage ? (
              <Typography.Text type="secondary">
                Kéo xuống để tải thêm
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">
                Không còn thông báo
              </Typography.Text>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={notificationList}
      trigger={["click"]}
      placement="bottom"
      open={visible}
      onOpenChange={setVisible}
    >
      <Badge
        count={notifications.filter((n) => !n.isRead).length}
        size="default"
      >
        <Button shape="circle" icon={<BellOutlined />} size="large" />
      </Badge>
    </Popover>
  );
};

export default Notification;
